import { Injectable } from '@angular/core';

import { translate } from '@jsverse/transloco';
import * as Highcharts from 'highcharts';

import moment from 'moment-timezone';

import { RGBAColorInput } from '@inmoment/the-kitchen/src/lib/Pickers/ColorPicker/ColorPicker.model';

import { ChartOptionConstants } from 'app/constants/constants';
import {
  DATE_TIME_WITHOUT_TIMEZONE_FORMAT,
  Filter,
  FilterService,
} from 'app/modules/collection/services/filter.service';
import {
  ComparisonHandlerService,
  DEFAULT_COMPARISON_COLOR_STRING,
} from 'app/modules/dashboard/services/comparison-handler-service';
import { Dashboard, Widget } from 'app/providers/bridge-api/dashboard.service';
import { EnableClassTelemetry } from 'app/services/otel.service';
import { ScatterPlotUtils } from 'app/utils/scatter-plot-utils';
import { StringBuilder } from 'app/utils/string-builder';
import { Utils } from 'app/utils/utils';
import { FieldService } from '../../../providers/storage-api/field.service';
import {
  BreakdownOptions,
  MetricEnum,
  WidgetChartOptions,
} from '../components/widget-chart/widget-chart.component';
import { HighchartsOptions } from './chart-display.service';

import {
  AbstractFeatureChartReport,
  AbstractChartReport,
} from './chart-report/chart-report-abstract/chart-report-abstract';

export interface DocumentCountOptions {
  xTitle: string;
  yTitle: string;
}

interface SentimentPolarityOptions {
  xTitle: string;
  yTitle: string;
}

interface DateHistogramOptions {
  xTitle: string;
  yTitle: string;
  filter?: (key: string, value: any) => any;
  seriesName?: string;
}

interface GaugeComparisonData {
  readonly metricValue: number;
  readonly metricText: string;
  readonly iconColor: string;
  readonly isIncrease: boolean;
}

@EnableClassTelemetry()
@Injectable()
export class ChartDataService {
  /** Finest date interval the backend date histogram supports for journey timeline drilling. */
  public static readonly JOURNEY_TIMELINE_FINEST_DATE_INTERVAL = 'minute' as const;
  private static readonly DONUT_LABELS = {
    DEFAULT_RADIUS: 100,
    METRIC_FONT_RATIO: 0.36,
    METRIC_FONT_MIN: 18,
    METRIC_FONT_MAX: 46,
    docCountFontRatio: 0.33,
    docCountFontMin: 10,
    docCountFontMax: 16,
    docCountYOffsetRatio: 0.95,
    comparisonMetricYOffsetRatio: 0.8,
    comparisonMetricFontRatio: 0.4,
    comparisonMetricFontMin: 12,
    GAP_RATIO: 0.12,
    GAP_MIN: 4,
    GAP_MAX: 8,
    TOOLTIP_Y_OFFSET: 32,
  } as const;
  static resetZoom(widget: Widget<WidgetChartOptions>) {
    if (!widget.payload.zoomDateFilter) {
      return;
    }
    ChartDataService.clearZoomFilter(widget);
    if (widget.payload.refresh) {
      widget.payload.refresh(widget.id);
    }
  }

  static clearZoomFilter(widget: Widget<WidgetChartOptions>): boolean {
    if (!widget.payload.zoomDateFilter) {
      return false;
    }
    const and = widget.payload.filters.dates.and;
    const idx = and.findIndex((filter) => filter.value === widget.payload.zoomDateFilter);
    delete widget.payload.zoomDateFilter;
    if (idx === -1) {
      return false;
    }
    and.splice(idx, 1);
    return true;
  }

  static setZoom(
    widget: Widget<WidgetChartOptions>,
    timezone: string,
    event: Highcharts.AxisSetExtremesEventObject
  ) {
    if (!event.userMax || !event.userMin) {
      // this can happen on browser resize and we want to ignore
      return;
    }
    ChartDataService.clearZoomFilter(widget);

    const chart = (event.target as any).chart;
    let previousBucket = event.min;
    let nextBucket = event.max;
    let bucketInterval = FilterService.defaultDateIntervalStep(widget.payload.dateInterval);
    if (chart.series[0].data.length > 1) {
      bucketInterval = chart.series[0].data[1].x - chart.series[0].data[0].x;
    }

    for (const series of chart.series) {
      previousBucket =
        series.data.find(
          (point) =>
            point.x - bucketInterval / 2 <= previousBucket &&
            point.x + bucketInterval / 2 > previousBucket
        )?.x ?? previousBucket;
      nextBucket =
        series.data.find((point) => point.x <= nextBucket && point.x + bucketInterval > nextBucket)
          ?.x ?? nextBucket;
    }

    const minDate = moment.tz(previousBucket, timezone).format(DATE_TIME_WITHOUT_TIMEZONE_FORMAT);
    const maxDate = moment
      .tz(nextBucket + bucketInterval, timezone)
      .subtract(1, 'millisecond')
      .format(DATE_TIME_WITHOUT_TIMEZONE_FORMAT);
    const dateFilter = `daterange/${minDate}/${maxDate}`;
    const f: Filter = { analyticItem: 'createdAt', key: 'daterange', value: dateFilter };
    widget.payload.filters.dates.and.push(f);
    widget.payload.zoomDateFilter = dateFilter;

    if (widget.payload.refresh) {
      widget.payload.refresh(widget.id);
    }
  }

  public static formatDocumentCountData(response, widget, totalDocumentCount) {
    const data = [];
    const input = Object.assign({}, response.items);
    let xArray = [];
    let yArray = [];

    if (response.reportName === 'featureTopThemes') {
      if (widget.payload.type === 'wordcloud') {
        for (const key of Object.getOwnPropertyNames(input.count)) {
          let value = 0;
          for (const subkey of Object.getOwnPropertyNames(input.count[key])) {
            value += input.count[key][subkey];
          }
          data.push([value, key]);
        }
      } else {
        if (response.currentPeriodData) {
          for (const key of Object.getOwnPropertyNames(response.currentPeriodData.items)) {
            let value = 0;
            if (input.hasOwnProperty(key)) {
              for (const subkey of Object.getOwnPropertyNames(input[key])) {
                value += input[key][subkey];
              }
            }
            data.push([value, key]);
          }
        } else {
          for (const key of Object.getOwnPropertyNames(input)) {
            let value = 0;
            for (const subkey of Object.getOwnPropertyNames(input[key])) {
              value += input[key][subkey];
            }
            data.push([value, key]);
          }
        }
      }
    } else {
      const collectDocumentCountData = (counts: any): void => {
        for (const key of Object.getOwnPropertyNames(counts)) {
          const count = input.count[key] ?? 0;
          const docCount = input.docCount?.[key];
          const value = docCount ? { sentimentScore: count, documentCount: docCount } : count;
          data.push([value, key]);
        }
      };

      if (response.currentPeriodData) {
        collectDocumentCountData(response.currentPeriodData.items.count);
      } else {
        collectDocumentCountData(input.count);
      }
    }

    data.sort((a, b) => {
      const valueA = typeof a[0] === 'object' ? a[0].sentimentScore : a[0];
      const valueB = typeof b[0] === 'object' ? b[0].sentimentScore : b[0];
      if (valueA < valueB) {
        return 1;
      }
      if (valueA > valueB) {
        return -1;
      }
      return 0;
    });

    for (let i = 0; i < data.length; ++i) {
      const entry = data[i][0];

      xArray.push(data[i][1]);
      yArray.push({
        y: entry.sentimentScore ?? entry,
        x: i,
        documentCount: entry.documentCount ?? entry,
        pointId: data[i][1],
        id: data[i][1],
      });
    }

    if (widget.payload.limit !== -1) {
      xArray = xArray.slice(0, widget.payload.limit);
      yArray = yArray.slice(0, widget.payload.limit);
    }

    if (widget.payload.aggregator === 'percentage') {
      yArray.forEach((yData) => (yData.y = (yData.y * 100) / totalDocumentCount));
    }

    return { xArray, yArray };
  }

  public static formatDocumentBulkCountData(
    widget: Widget<WidgetChartOptions>,
    totalDocumentCount: number,
    comparisonBucketCount: number
  ) {
    const xArray = ['Documents'];
    const yArray = [
      {
        documentCount: comparisonBucketCount ?? totalDocumentCount,
        id: 'Documents',
        pointId: 'Documents',
        x: 0,
        y: comparisonBucketCount ?? totalDocumentCount,
      },
    ];

    if (widget.payload.aggregator === 'percentage') {
      yArray.forEach((yData) => (yData.y = (yData.y * 100) / totalDocumentCount));
    }

    return { xArray, yArray };
  }

  public static forDocumentCount(
    dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    options: DocumentCountOptions,
    reportOptions?: WidgetChartOptions
  ): HighchartsOptions {
    if (!response.items) {
      return null;
    }
    const hasMultiBucketComparison = response.comparisonBuckets?.length > 1;
    const currentPeriodData =
      response.currentPeriodData ?? response.comparisonBuckets?.[0] ?? response;

    const currentPeriodTotalDocCount =
      reportOptions?.currentPeriodDocumentCount ??
      reportOptions?.comparisonBucketsCounts?.[0] ??
      reportOptions?.filteredDocumentCount;

    const formattedData = ChartDataService.formatDocumentCountData(
      currentPeriodData,
      widget,
      currentPeriodTotalDocCount
    );

    reportOptions?.selectedItems
      ?.filter((item) => !formattedData.xArray.includes(item))
      .forEach((item) => {
        formattedData.xArray.push(item);
        formattedData.yArray.push({
          x: formattedData.xArray.length - 1,
          y: undefined,
          pointId: item,
          id: item,
          documentCount: undefined,
        });
      });

    const xArray = formattedData.xArray;
    const yArray = formattedData.yArray;

    if (hasMultiBucketComparison) {
      formattedData.yArray.forEach(
        (fy) => (fy.id += `-${dashboard.comparisons[widget.payload.comparison].filters[0].label}`)
      );
    }

    const result = {
      xAxis: { categories: xArray },
      yAxis: { title: { text: options.yTitle } },
      series: [
        {
          showInLegend: false,
          name: options.xTitle,
          data: yArray,
        },
      ],
      tooltip: {},
    };

    if (widget.payload.aggregator === 'percentage') {
      result.yAxis.title.text = translate('widgets.charting.documents-percentage');
    }

    if (response.currentPeriodData) {
      const formattedPreviousData = ChartDataService.formatDocumentCountData(
        response,
        widget,
        reportOptions?.previousPeriodDocumentCount
      );

      result.series.push({
        showInLegend: true,
        name: translate('widgets.charting.previous'),
        data: formattedPreviousData.yArray,
      });
      result.series[0].name = translate('widgets.charting.current');
      result.series[0].showInLegend = true;
    } else if (response.comparisonBuckets) {
      result.series[0].showInLegend = hasMultiBucketComparison;

      const swapOrInsert = (arr, fromIndex, toIndex, def) => {
        if (toIndex === -1) {
          arr.splice(fromIndex, 0, def);
          return;
        }

        const fy = arr[fromIndex] ?? def;
        arr[fromIndex] = arr[toIndex];
        arr[toIndex] = fy;
      };

      for (let i = 1; i < response.comparisonBuckets.length; i++) {
        const formattedBucketData = ChartDataService.formatDocumentCountData(
          response.comparisonBuckets[i],
          widget,
          reportOptions.comparisonBucketsCounts[i]
        );

        for (let index = 0; index < xArray.length; index++) {
          const fi = formattedBucketData.xArray.indexOf(xArray[index]);
          if (fi !== index) {
            swapOrInsert(formattedBucketData.yArray, index, fi, {
              x: index,
              y: undefined,
              pointId: xArray[index],
              id: xArray[index],
              documentCount: undefined,
            });
            swapOrInsert(formattedBucketData.xArray, index, fi, xArray[index]);
          }
        }

        formattedBucketData.yArray.forEach((fy, idx) => (fy.x = idx));
        if (hasMultiBucketComparison) {
          formattedBucketData.yArray.forEach(
            (fy) =>
              (fy.id += `-${dashboard.comparisons[widget.payload.comparison].filters[i].label}`)
          );
        }

        result.series.push({
          showInLegend: true,
          name: undefined,
          data: formattedBucketData.yArray,
        });

        formattedBucketData.xArray.forEach((x) => {
          if (!result.xAxis.categories.includes(x)) {
            result.xAxis.categories.push(x);
          }
        });
      }
    }

    if (widget.payload.type === 'sentimentScore') {
      const aggregatorSuffix = widget.payload.aggregator.includes('AVG') ? 'Avg' : 'Sum';
      result.tooltip = {
        formatter() {
          let tooltip =
            translate('widgets.charting.tooltip.name-part', { name: this.point.pointId }) +
            '<br/>' +
            translate('widgets.charting.tooltip.sentiment-aggregator-part', {
              aggregatorSuffix,
              sentiment: this.y.toFixed(1),
            }) +
            '<br/>';

          if (typeof this.point.documentCount !== 'undefined') {
            tooltip += translate('widgets.charting.tooltip.count-part', {
              count: Utils.addThousandSeparator(this.point.documentCount),
            });
          }

          return tooltip;
        },
      };
    }

    for (let i = result.xAxis.categories.length - 1; i > 0; i--) {
      if (result.series.every((s) => s.data[i]?.y === undefined)) {
        result.series.forEach((s) => s.data.splice(i, 1));
      }
    }

    return result;
  }

  public static forSentimentPolarity(
    dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    options: SentimentPolarityOptions
  ): HighchartsOptions {
    if (!response.items) {
      return null;
    }

    const data = [];

    for (const key of Object.getOwnPropertyNames(response.items.count)) {
      const value = response.items.count[key];

      data.push([key, value]);
    }

    data.sort((a, b) => {
      let sumA = 0;
      let sumB = 0;
      for (const key of Object.getOwnPropertyNames(a[1])) {
        sumA += a[1][key];
      }
      for (const key of Object.getOwnPropertyNames(b[1])) {
        sumB += b[1][key];
      }
      return sumB - sumA;
    });

    const { events, shouldBeVisible } = FilterService.createSentimentChartFilterEvents(widget);
    const showAsStacked = widget.payload.showAsStacked || widget.payload.graphType === 'pie';
    const showByPercentage = widget.payload.aggregator === 'percentage';
    const sumTotal = (obj) => (<any>Object).values(obj).reduce((a: number, b: number) => a + b, 0);

    const countDataTransformer = (value, sentiment) => ({
      pointId: value[0],
      y: value[1][sentiment],
      documentCount: value[1][sentiment],
    });
    const percentDataTransformer = (value: number, sentiment: string) => ({
      pointId: value[0],
      y: (value[1][sentiment] * 100) / sumTotal(value[1]),
      documentCount: value[1][sentiment],
    });

    const { series, xArray, tooltipTotals } = ChartDataService.generateSentimentSeries(
      dashboard.payload.sentimentScale.toString(),
      data,
      shouldBeVisible,
      showByPercentage && !showAsStacked ? percentDataTransformer : countDataTransformer,
      !showByPercentage && !showAsStacked,
      showAsStacked,
      showByPercentage
    );

    const result = {
      xAxis: { categories: xArray },
      yAxis: { title: { text: options.yTitle } },
      series,
      tooltip: {
        shared: false,
        formatter() {
          const index = this.series.data.indexOf(this.point);
          const polarity = this.series.name === 'Total volume' ? 'Total' : this.series.name;
          const total = this.series.chart.series
            .filter((s) => s.name !== 'Total volume')
            .reduce((prev, curr) => prev + curr.data[index]?.y, 0);
          // Probably due to highchart update 'name' field disappeared and
          // 'this.x' is not relevant (it's a number and compare a number with a string pointId is not clever)
          // so we are better off using pointId to compare with pointId
          const name = this.point.pointId;
          let percent: number;
          const itemTotals = tooltipTotals.find((item) => item.pointId === name);

          if (showAsStacked) {
            percent = total ? Math.round((this.y * 100) / total) : this.percentage;
          } else {
            percent = showByPercentage
              ? Utils.roundNearest((this.y * 100) / itemTotals.total)
              : Utils.roundNearest((itemTotals[polarity.toLowerCase()] / itemTotals.total) * 100);
          }
          const count =
            showByPercentage && !showAsStacked
              ? Utils.addThousandSeparator(itemTotals[polarity.toLowerCase()])
              : Utils.addThousandSeparator(this.y);

          if ((showAsStacked && showByPercentage) || showByPercentage) {
            return `${translate('widgets.charting.tooltip.name-part', { name })}<br>
                  ${translate('widgets.charting.tooltip.count-part', {
                    count,
                  })}<br>
                `;
          }

          return `${translate('widgets.charting.tooltip.name-part', { name })}<br>
          ${translate('widgets.charting.tooltip.polarity-part', { polarity })}<br>
          ${translate('widgets.charting.tooltip.count-part', {
            count,
          })}<br>
          ${translate('widgets.charting.tooltip.percentage-part', {
            percentage: percent.toFixed(0),
          })}`;
        },
      },
      plotOptions: {
        column: {
          stacking: widget.payload.showAsStacked ? 'normal' : '',
        },
        series: {
          stacking: widget.payload.showAsStacked ? 'normal' : '',
          events,
        },
      },
    } as any;

    if ((showAsStacked || showByPercentage) && widget.payload.graphType !== 'pie') {
      result.yAxis = [
        { ...result.yAxis, gridLineWidth: 0 },
        {
          title: {
            text: translate('widgets.charting.document-count'),
          },
          opposite: true,
        },
      ];
    }

    result.series.forEach((s) =>
      s.data.forEach((d) => {
        d.id = `${d.pointId}-${s.name}`;
      })
    );

    if (widget.payload.graphType === 'pie') {
      const sentimentSeries = series.filter((s) => s.name.toLowerCase() !== 'total volume');
      result.xAxis.categories = sentimentSeries.map((category) => category.name);
      result.series = [
        {
          name: xArray[0],
          data: sentimentSeries.map((value) => ({ ...value.data[0], name: value.name })),
        },
      ];
      result.plotOptions = {
        column: {
          stacking: '',
        },
        series: {
          stacking: '',
          events,
        },
        pie: {
          colors: sentimentSeries.map((value) => value.color),
          // NOTE: Kitchen stuff must be overriding the defaults, need to set them again
          dataLabels: {
            style: {
              textOutline: 0,
              color: 'black',
            },
          },
        },
      };
      result.tooltip.formatter = function () {
        const total = this.series.data.reduce((prev, curr) => prev + (curr.y || 0), 0);
        const percent = Math.round((this.y * 100) / total);
        return `${translate('widgets.charting.tooltip.polarity-part', {
          polarity: this.point.name,
        })}<br>
        ${translate('widgets.charting.tooltip.count-part', {
          count: Utils.addThousandSeparator(this.y),
        })}<br>
        ${translate('widgets.charting.tooltip.percentage-part', {
          percentage: percent.toFixed(0),
        })}`;
      };
    }

    if (widget.payload.aggregator === 'percentage') {
      if (Array.isArray(result.yAxis)) {
        result.yAxis[0].title.text = translate('widgets.charting.documents-percentage');
        result.yAxis[0].max = 100;
      } else {
        result.yAxis.title.text = translate('widgets.charting.documents-percentage');
        result.yAxis.max = 100;
      }
      result.plotOptions.column.stacking = widget.payload.showAsStacked ? 'percent' : '';
      result.plotOptions.series.stacking = widget.payload.showAsStacked ? 'percent' : '';

      (result.tooltip as any).pointFormat = `
        <span style="color:{series.color}">{series.name}</span>:
        <b>{point.y}</b> ({point.percentage:.0f}%)
        <br/>
      `;
    }

    return result;
  }

  public static forSentimentPolarityOverTime(
    dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    options: SentimentPolarityOptions
  ): HighchartsOptions {
    if (!response.items) {
      return null;
    }

    const data = [];

    for (const key of Object.getOwnPropertyNames(response.items)) {
      const value = response.items[key];

      data.push([Date.parse(key), value]);
    }

    data.sort((a, b) => a[0] - b[0]);

    const { events, shouldBeVisible } = FilterService.createSentimentChartFilterEvents(widget);

    const dataTransformer = (value, sentiment) => {
      if (widget.payload.aggregator === 'percentage_time') {
        const sum = Object.values(value[1] as { [key: string]: number }).reduce(
          (valueSum, current) => valueSum + current,
          0
        );

        const count = value[1][sentiment];

        return { x: value[0], y: Math.round((count * 100) / sum), sum, count };
      }

      return { x: value[0], y: value[1][sentiment], count: value[1][sentiment] };
    };

    const { series } = ChartDataService.generateSentimentSeries(
      dashboard.payload.sentimentScale.toString(),
      data,
      shouldBeVisible,
      dataTransformer,
      false,
      widget.payload.showAsStacked,
      false
    );

    const stacking =
      widget.payload.graphType !== 'line' && widget.payload.graphType !== 'spline'
        ? 'normal'
        : undefined;

    const tickRange = ChartDataService.minDateInterval(widget);

    const result = {
      chart: {
        zoomType: dashboard?.locked ? false : 'x',
        type: widget.payload.graphType,
      },
      xAxis: {
        type: 'datetime',
        events: {
          afterSetExtremes: ChartDataService.setZoom.bind(null, widget, dashboard.payload.timezone),
        },
        minRange: ChartDataService.getMinRange(tickRange, series),
        tickInterval: ChartDataService.getTickInterval(tickRange, series, widget),
        labels: ChartDataService.getDateLabelsOptions(widget),
      },
      yAxis: { title: { text: options.yTitle } },
      series,
      plotOptions: {
        column: {
          stacking,
        },
        series: {
          stacking,
          events,
        },
      },
      tooltip: {
        shared: false,
        formatter() {
          const polarity = this.series.name;

          let tooltip = `${translate('widgets.charting.tooltip.date-part', {
            date: Highcharts.dateFormat(
              AbstractFeatureChartReport.DEFAULT_DATE_TIME_FORMAT,
              +this.x
            ),
          })}<br/>
          ${translate('widgets.charting.tooltip.polarity-part', {
            polarity,
          })} <span style="color: ${this.color}">${AbstractChartReport.getSeriesSymbolChar(
            this.series.symbol
          )}</span><br/>`;

          if (widget.payload.aggregator === 'percentage_time') {
            tooltip += `${translate('widgets.charting.tooltip.percentage-count-total-part', {
              percentage: this.y,
              count: Utils.addThousandSeparator(this.point.count),
              total: this.point.sum,
            })}`;
          } else {
            tooltip += `${translate('widgets.charting.tooltip.count-part', {
              count: Utils.addThousandSeparator(this.y),
            })}`;
          }

          return tooltip;
        },
      },
    };

    result.series.forEach((s) =>
      s.data.forEach((d) => {
        d.id = `${d.x}-${s.name}`;
      })
    );

    const zoomOptions = ChartDataService.getZoomButtonOption(widget, result);
    if (zoomOptions) {
      Object.keys(zoomOptions).forEach((k) => (result[k] = zoomOptions[k]));
    }

    return result;
  }

  private static generateSentimentSeries(
    sentimentScale,
    data: any[],
    shouldBeVisible: (value: string, isTotalValue?: boolean) => boolean,
    dataTransformer: (value, sentiment) => any,
    showTotalColumn: boolean,
    isStacked: boolean,
    showByPercentage: boolean
  ) {
    let series = [];
    const xArray = [];
    const sumTotal = (obj) => Object.values(obj).reduce((a: number, b: number) => a + b, 0);
    const tooltipTotals = [];

    switch (sentimentScale) {
      default:
      case '':
      case '3':
        series = [
          { name: 'Positive', color: '#55BF3B', visible: shouldBeVisible('positive'), data: [] },
          { name: 'Neutral', color: '#929292', visible: shouldBeVisible('neutral'), data: [] },
          { name: 'Negative', color: '#DF5353', visible: shouldBeVisible('negative'), data: [] },
        ];

        if (showTotalColumn) {
          series.push({
            name: 'Total volume',
            color: '#7CB5EC',
            visible: shouldBeVisible('Total volume', true),
            data: [],
            custom: {
              insightSelectable: false,
            },
          });
        }

        if (isStacked || showByPercentage) {
          series.push({
            name: 'Total volume',
            color: '#000000',
            visible: shouldBeVisible('Total volume', true),
            data: [],
            type: 'line',
            marker: {
              lineWidth: 1,
              lineColor: '#000000',
              fillColor: '#000000',
            },
            custom: {
              insightSelectable: false,
            },
          });
        }

        for (const value of data) {
          xArray.push(value[0]);
          series[0].data.push(dataTransformer(value, 'positive'));
          series[1].data.push(dataTransformer(value, 'neutral'));
          series[2].data.push(dataTransformer(value, 'negative'));
          tooltipTotals.push({
            pointId: value[0],
            total: sumTotal(value[1]),
            positive: value[1]['positive'],
            negative: value[1]['negative'],
            neutral: value[1]['neutral'],
          });

          if (showTotalColumn || isStacked || showByPercentage) {
            series[3].data.push({ pointId: value[0], y: sumTotal(value[1]) });
          }
          if (showByPercentage) {
            series[3].stacking = null;
            series[3].yAxis = 1;
          }
        }

        break;

      case '5':
        series = [
          {
            name: 'Very Positive',
            color: '#55BF3B',
            visible: shouldBeVisible('very positive'),
            data: [],
          },
          {
            name: 'Somewhat Positive',
            color: '#BFFFB2',
            visible: shouldBeVisible('somewhat positive'),
            data: [],
          },
          { name: 'Neutral', color: '#cccccc', visible: shouldBeVisible('neutral'), data: [] },
          {
            name: 'Somewhat Negative',
            color: '#FE9999',
            visible: shouldBeVisible('somewhat negative'),
            data: [],
          },
          {
            name: 'Very Negative',
            color: '#DF5353',
            visible: shouldBeVisible('very negative'),
            data: [],
          },
        ];
        if (showTotalColumn) {
          series.push({
            name: 'Total volume',
            color: '#7CB5EC',
            visible: shouldBeVisible('Total volume', true),
            data: [],
            custom: {
              insightSelectable: false,
            },
          });
        }

        if (isStacked || showByPercentage) {
          series.push({
            name: 'Total volume',
            color: '#000000',
            visible: shouldBeVisible('Total volume', true),
            data: [],
            type: 'line',
            marker: {
              lineWidth: 1,
              lineColor: '#000000',
              fillColor: '#000000',
            },
            custom: {
              insightSelectable: false,
            },
          });
        }

        for (const value of data) {
          xArray.push(value[0]);
          series[0].data.push(dataTransformer(value, 'very positive'));
          series[1].data.push(dataTransformer(value, 'somewhat positive'));
          series[2].data.push(dataTransformer(value, 'neutral'));
          series[3].data.push(dataTransformer(value, 'somewhat negative'));
          series[4].data.push(dataTransformer(value, 'very negative'));
          tooltipTotals.push({
            pointId: value[0],
            total: sumTotal(value[1]),
            ['very positive']: value[1]['very positive'],
            ['somewhat positive']: value[1]['somewhat positive'],
            neutral: value[1]['neutral'],
            ['somewhat negative']: value[1]['somewhat negative'],
            ['very negative']: value[1]['very negative'],
          });

          if (showTotalColumn || isStacked || showByPercentage) {
            series[5].data.push({ pointId: value[0], y: sumTotal(value[1]) });
          }
          if (showByPercentage) {
            series[5].stacking = null;
            series[5].yAxis = 1;
          }
        }
        break;
    }

    return { series, xArray, tooltipTotals };
  }

  //Return the minimum date interval of the selected range.
  //There was an issue where user selected intervals were breaking the highcharts range.
  //This also caused the no data found issues, now we can zoom all the way in.
  //Defaults to an hour like before.
  public static getDateIntervalMs(dateInterval: string): number {
    switch (dateInterval) {
      case 'minute':
        return 60000;
      case 'hour':
        return 3600000;
      case 'day':
        return 86400000;
      case 'week':
        return 604800000;
      case 'month':
        return 2678400000;
      case 'quarter':
        return 7948800000;
      case 'year':
        return 31536000000;
      default:
        return 3600000;
    }
  }

  private static minDateInterval(
    widget: Widget<WidgetChartOptions>,
    dateIntervalOverride?: string
  ) {
    return ChartDataService.getDateIntervalMs(dateIntervalOverride ?? widget.payload.dateInterval);
  }

  /**
   * Builds the journey touchpoints timeline points straight from the backend date histogram
   * buckets. Each bucket becomes a single marker whose badge shows the bucket document count;
   * drilling into a bucket re-queries the report at the next finer date interval.
   */
  private static buildJourneyTouchpointPoints(bucket: any, subjectId?: string): any[] {
    return Object.keys(bucket.items)
      .map((dateKey) => ({ dateKey, x: Date.parse(dateKey) }))
      .sort((left, right) => left.x - right.x)
      .map(({ dateKey, x }) => {
        const item = bucket.items[dateKey];
        const pointId = subjectId ? `${subjectId}-journey-${x}` : `journey-${x}`;
        return {
          x,
          y: 0,
          id: pointId,
          documentCount: item.metadata?.[0]?.docs ?? 0,
          contentChannels: item.contentChannels ?? [],
          isCluster: false,
          ...(subjectId && { subjectId }),
        };
      })
      .filter((point) => point.documentCount > 0);
  }

  /**
   * Next finer date interval used when drilling into a journey timeline bucket.
   * Returns null once the finest supported backend interval is reached.
   */
  public static getNextJourneyTimelineDateInterval(dateInterval: string): string | null {
    switch (dateInterval) {
      case 'year':
      case 'quarter':
        return 'month';
      case 'month':
      case 'week':
        return 'day';
      case 'day':
        return 'hour';
      case 'hour':
        return 'minute';
      default:
        return null;
    }
  }

  /**
   * Coarsest interval below `dateInterval` that splits `timestamps` into more than one bucket.
   * Returns null when no interval separates them, which is the case for documents sharing a
   * timestamp: drilling into them would keep rendering the same single marker.
   */
  public static getCoarsestSplittingDateInterval(
    timestamps: number[],
    dateInterval: string,
    timezone: string
  ): string | null {
    let candidate = ChartDataService.getNextJourneyTimelineDateInterval(dateInterval);
    while (candidate) {
      const bucketStarts = new Set(
        timestamps.map((timestamp) =>
          ChartDataService.getJourneyTimelineBucketStart(timestamp, candidate, timezone)
        )
      );
      if (bucketStarts.size > 1) {
        return candidate;
      }
      candidate = ChartDataService.getNextJourneyTimelineDateInterval(candidate);
    }
    return null;
  }

  /**
   * Start of the bucket a timestamp falls into, matching how the backend date histogram groups it.
   * Only used to work out whether an interval would split a set of timestamps; chart data itself
   * is bucketed by the backend.
   */
  private static getJourneyTimelineBucketStart(
    timestamp: number,
    dateInterval: string,
    timezone: string
  ): number {
    const date = moment.tz(timestamp, timezone);
    switch (dateInterval) {
      case 'minute':
        return date.startOf('minute').valueOf();
      case 'hour':
        return date.startOf('hour').valueOf();
      case 'day':
        return date.startOf('day').valueOf();
      case 'week':
        return date.startOf('week').valueOf();
      case 'month':
        return date.startOf('month').valueOf();
      case 'quarter':
        return date.startOf('quarter').valueOf();
      case 'year':
        return date.startOf('year').valueOf();
      default:
        return date.startOf('hour').valueOf();
    }
  }

  public static getDateLabelsOptions(
    widget: Widget<WidgetChartOptions>,
    dateIntervalOverride?: string
  ) {
    switch (dateIntervalOverride ?? widget.payload.dateInterval) {
      case 'hour':
        return { format: `{value: %I:%M %p}` };
      case 'minute':
        return { format: `{value: %I:%M %p}` };
      case 'day':
        return { format: `{value: %b %d}` };
      case 'week':
        const weekOfLabel = translate('widgets.charting.tooltip.date-part-week-of');
        const showWeekPrefix = widget.payload.showWeekPrefix !== false;
        if (!showWeekPrefix) {
          return { format: `{value: %b %d}` };
        }
        return {
          useHTML: true,
          formatter() {
            const combineWeekLabel: string[] = [];
            combineWeekLabel.push(weekOfLabel);
            combineWeekLabel.push(Highcharts.dateFormat('%b %d', this.value));
            return combineWeekLabel.join('<br/>');
          },
        };
      case 'month':
        return { format: `{value: %b %Y}` };
      case 'quarter':
        return {
          formatter(chartDataPoint) {
            const date = new Date(chartDataPoint.value);
            const quarter = Math.floor(date.getMonth() / 3) + 1;
            const year = date.getFullYear() % 100;
            return `Q${quarter}'${('0' + year).slice(-2)}`;
          },
        };
      default:
        return {};
    }
  }

  public static forDateHistogram(
    dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    options: DateHistogramOptions,
    _reportOptions?: WidgetChartOptions
  ): HighchartsOptions {
    if (!response.items) {
      return null;
    }

    const input = Object.assign({}, response.items);
    const data = [];
    const dates = [];
    const series = [];
    const documentCountByDate = {};

    if (options.filter) {
      for (const key of Object.getOwnPropertyNames(input)) {
        const value = input[key];
        const filtered = options.filter(key, value);

        if (typeof filtered === 'undefined') {
          delete input[key];
        } else {
          input[key] = filtered;
        }
      }
    }

    for (const key of Object.getOwnPropertyNames(input)) {
      let value = input[key];
      if (response.reportName === 'taxonomyTopFeaturesOverTime') {
        value = input[key]['count'];
      }
      const parsed = Date.parse(key);

      data.push([parsed, value]);
      dates.push(parsed);
    }
    dates.sort((a, b) => a - b);

    const arrangedData: any = {};

    for (const item of data) {
      const date = item[0];
      const counts = item[1];

      if (
        Object.getOwnPropertyNames(counts).some((pn) => ['documentCount', 'documents'].includes(pn))
      ) {
        documentCountByDate[date] = counts.documentCount ?? counts.documents;
      }

      for (const key of Object.getOwnPropertyNames(counts).filter((k) => k !== 'documentCount')) {
        const value = counts[key];

        if (typeof arrangedData[key] === 'undefined') {
          arrangedData[key] = {};
        }

        if (value !== 'NaN' && value !== 'Infinity') {
          arrangedData[key][date] = value;
        }
      }
    }

    for (const key of Object.getOwnPropertyNames(arrangedData)) {
      const contents = arrangedData[key];
      const serie = [];

      for (let date of dates) {
        date = date.valueOf();
        const value = contents[date];

        let serieItem;
        if (typeof value !== 'undefined') {
          serieItem = {
            x: date,
            y: value.sentimentScore ?? value.documentCount ?? value,
            documentCount: value.documentCount,
            pointId: key,
            id: `${date}-${key}`,
            key,
            selected: value.selected,
          };

          if (Object.getOwnPropertyNames(documentCountByDate).length) {
            serieItem.totalDocumentCountByDate = documentCountByDate[date];
          }
        } else if (widget.payload.zeroPadding) {
          serieItem = { x: date, y: 0, documentCount: 0 };
        }

        if (serieItem) {
          if (widget.payload.aggregator === 'percentage_time') {
            // For documents we need to use the document count
            if (widget.payload.analyticItem === 'documents') {
              serieItem.documentCount = serieItem.y;
            }
            serieItem.y = Math.round((serieItem.y * 100) / serieItem.totalDocumentCountByDate);
          }

          serie.push(serieItem);
        }
      }

      if (response.reportName === 'taxonomyTopFeaturesOverTime') {
        let displayName = key;
        if (response.extra?.taxonomyNodes?.[key]) {
          displayName = response.extra.taxonomyNodes[key].name;
        }
        series.push({
          showInLegend: widget.payload.analyticItem !== 'documents',
          name: displayName,
          data: serie,
        });
      } else {
        series.push({
          showInLegend: widget.payload.analyticItem !== 'documents',
          name: options.seriesName ? options.seriesName : key,
          data: serie,
        });
      }
    }

    const tickRange = ChartDataService.minDateInterval(widget);

    const result = {
      chart: {
        zoomType: dashboard?.locked ? false : 'x',
        type: widget.payload.graphType,
      },
      xAxis: {
        type: 'datetime',
        events: {
          afterSetExtremes: ChartDataService.setZoom.bind(null, widget, dashboard.payload.timezone),
        },
        // if we have singular date so every item falls under one date we extend the tickrange as well as apply the tickRange to tickInterval
        // for more see: DASH-9633
        minRange: ChartDataService.getMinRange(tickRange, dates),
        tickInterval: ChartDataService.getTickInterval(tickRange, dates, widget),
        labels: ChartDataService.getDateLabelsOptions(widget),
      },
      yAxis: { title: { text: options.yTitle } },
      series,
      tooltip: {},
      dateInterval: widget.payload.dateInterval,
    };

    const zoomOptions = ChartDataService.getZoomButtonOption(widget, result);
    if (zoomOptions) {
      Object.keys(zoomOptions).forEach((k) => (result[k] = zoomOptions[k]));
    }

    return result;
  }

  private static getMinRange(tickRange: number, dates: Array<number>): number {
    if (dates.length === 1) {
      return tickRange * 3;
    }
    return tickRange;
  }

  private static getTickInterval(
    tickRange: number,
    dates: Array<number>,
    widget: Widget<any>,
    dateIntervalOverride?: string
  ): number | undefined {
    const dateInterval = dateIntervalOverride ?? widget.payload.dateInterval;
    if (dates.length === 1 || dateInterval === 'quarter') {
      return tickRange;
    }
    return undefined;
  }

  public static forNumericMetadataOverTime(
    dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    options: DateHistogramOptions,
    _reportOptions?: WidgetChartOptions
  ): HighchartsOptions {
    const bucketedResponse = response.comparisonBuckets ?? [response];
    const hasMultiBucketComparison = bucketedResponse.length > 1;

    const nonPcFields = widget.payload.numericMetadataFields
      .map((f, i) => ({ label: f.label, scoring: f.scoring, index: i }))
      .filter((f) => f.scoring === 'avg' || f.scoring === 'sum');
    const pcFields = widget.payload.numericMetadataFields
      .map((f, i) => ({ label: f.label, scoring: f.scoring, index: i }))
      .filter((f) => f.scoring === 'top' || f.scoring === 'bottom' || f.scoring === 'nps');
    // if it has non pc fields it's always left axis
    const yAxis = [
      {
        title: {
          text: nonPcFields.length
            ? nonPcFields.map((f) => f.label).join(', ')
            : pcFields.map((f) => f.label).join(', '),
        },
        labels: {
          format: '{value}' + nonPcFields.length ? '' : '%',
        },
        opposite: false,
        isPercentage: !!pcFields.length && !nonPcFields.length,
      },
    ];

    if (nonPcFields.length && pcFields.length) {
      yAxis.push({
        title: {
          text: pcFields.map((f) => f.label).join(', '),
        },
        labels: {
          format: '{value}%',
        },
        opposite: true,
        isPercentage: true,
      });
    }

    const series = [];

    for (let bucketIndex = 0; bucketIndex < bucketedResponse.length; bucketIndex++) {
      const bucket = bucketedResponse[bucketIndex];
      const sortedDates = Object.keys(bucket.items)
        .map((date) => Date.parse(date))
        .sort((a, b) => a - b);

      const arrangedData = widget.payload.numericMetadataFields.map((_f, i) => {
        const metadataSeries = {};
        for (const date of Object.keys(bucket.items)) {
          const dateInMillis = Date.parse(date);
          metadataSeries[dateInMillis] = bucket.items[date].metadata[i];

          if (options.filter) {
            const filtered = options.filter(date, metadataSeries[date]);

            if (typeof filtered === 'undefined') {
              metadataSeries[dateInMillis] = undefined;
              continue;
            }
            metadataSeries[dateInMillis] = filtered;
          }
        }

        return metadataSeries;
      });

      for (let fieldIndex = 0; fieldIndex < arrangedData.length; fieldIndex++) {
        const axis = !nonPcFields.length || nonPcFields.some((f) => f.index === fieldIndex) ? 0 : 1;
        const numericMetadataField = widget.payload.numericMetadataFields.find(
          (_, i) => i === fieldIndex
        );
        const journeyTimelineRole = (widget.payload as any).journeyTimelineRole;
        const isJourneyTouchpointTimeline =
          journeyTimelineRole === 'touchpoints' && widget.payload.graphType === 'timeline';
        if (isJourneyTouchpointTimeline && fieldIndex > 0) {
          continue;
        }
        const rgba = numericMetadataField.color;
        const color = `rgba(${rgba.red}, ${rgba.green}, ${rgba.blue}, ${rgba.alpha})`;
        const tooltipSuffix = pcFields.some((f) => f.index === fieldIndex) ? '%' : '';

        const seriesData = [];
        for (const dateInMillis of sortedDates) {
          const values = arrangedData[fieldIndex][dateInMillis];
          if (!values) {
            continue;
          }

          let value;
          switch (numericMetadataField.scoring) {
            case 'top':
              value = values.topScore;
              break;
            case 'bottom':
              value = values.bottomScore;
              break;
            default:
              value = values.score;
              break;
          }

          if (value !== null && value !== 'Infinity' && value !== 'NaN') {
            let id = `${dateInMillis}-${numericMetadataField.label}`;
            if (hasMultiBucketComparison) {
              id += `-${
                dashboard.comparisons[widget.payload.comparison].filters[bucketIndex].label
              }`;
            }
            const dateKey = Object.keys(bucket.items).find((d) => Date.parse(d) === dateInMillis);
            const contentChannels = dateKey ? bucket.items[dateKey].contentChannels : undefined;
            seriesData.push({
              x: dateInMillis,
              y: value,
              id,
              documentCount: values.docs,
              contentChannels,
            });
          }
        }

        // Journey charts are bucketed by the backend date histogram: the report is re-executed at
        // the drilled date interval, so the response buckets are used as-is.
        let finalData = seriesData;
        if (isJourneyTouchpointTimeline) {
          finalData = ChartDataService.buildJourneyTouchpointPoints(
            bucket,
            (widget.payload as any).subjectValue
          );
        }

        series.push({
          name: numericMetadataField.label,
          yAxis: axis,
          data: finalData,
          color,
          tooltip: {
            valueSuffix: tooltipSuffix,
          },
          custom: {
            fieldId: numericMetadataField.fieldId,
          },
        });
      }
    }

    const expandedJourneyDateInterval = (widget.payload as any).expandedJourneyDateInterval as
      | string
      | undefined;
    const isNmotWithExpanded =
      (widget.payload as any).journeyTimelineRole === 'nmot' && expandedJourneyDateInterval;
    const touchpointHourInterval =
      expandedJourneyDateInterval === 'touchpoint' ? 'hour' : expandedJourneyDateInterval;
    const expandedIntervalOrDefault = isNmotWithExpanded
      ? touchpointHourInterval
      : widget.payload.dateInterval;
    const effectiveDateInterval = expandedIntervalOrDefault;
    const tickRange = ChartDataService.minDateInterval(widget, effectiveDateInterval);

    const isJourneyTimeline = Boolean((widget.payload as any).journeyTimelineRole);
    const shouldEnableZoom = !dashboard?.locked && !isJourneyTimeline;
    const xAxisConfig: Record<string, unknown> = {
      type: 'datetime',
      minRange: ChartDataService.getMinRange(tickRange, series),
      tickInterval: ChartDataService.getTickInterval(
        tickRange,
        series,
        widget,
        effectiveDateInterval
      ),
      labels: ChartDataService.getDateLabelsOptions(widget, effectiveDateInterval),
      // Journey timeline/NMOT charts must not round min/max out to the next "nice" tick:
      // Highcharts' default startOnTick/endOnTick would otherwise extend the axis beyond the
      // actual last data point, rendering extra empty day/week/month labels.
      ...(isJourneyTimeline && { startOnTick: false, endOnTick: false }),
    };
    if (shouldEnableZoom) {
      xAxisConfig.events = {
        afterSetExtremes: ChartDataService.setZoom.bind(null, widget, dashboard.payload.timezone),
      };
    }

    if (widget.payload.isDrilledDown) {
      const allX = series.flatMap((s) => (s.data as any[]).map((p) => p.x as number));
      if (allX.length > 0) {
        const xMin = allX.reduce((a, b) => Math.min(a, b), Infinity);
        const xMax = allX.reduce((a, b) => Math.max(a, b), -Infinity);
        const padding = (xMax - xMin) * 0.03 || tickRange;
        xAxisConfig.min = xMin - padding;
        xAxisConfig.max = xMax + padding;
      }
    }
    const expandedJourneyRange = (widget.payload as any).expandedJourneyRange as
      | { min: number; max: number }
      | undefined;
    const expandedJourneyDisplayRange = (widget.payload as any).expandedJourneyDisplayRange as
      | { min: number; max: number }
      | undefined;
    if ((widget.payload as any).journeyTimelineRole === 'nmot' && expandedJourneyRange) {
      const displayRange = expandedJourneyDisplayRange ?? expandedJourneyRange;
      xAxisConfig.min = displayRange.min;
      xAxisConfig.max = displayRange.max;
      xAxisConfig.tickInterval =
        expandedJourneyDateInterval && expandedJourneyDateInterval !== 'touchpoint'
          ? ChartDataService.getDateIntervalMs(expandedJourneyDateInterval)
          : undefined;
    }

    const isJourneyTouchpoints = (widget.payload as any).journeyTimelineRole === 'touchpoints';
    const result = {
      chart: {
        zoomType: shouldEnableZoom ? 'x' : false,
        type: widget.payload.graphType,
      },
      xAxis: xAxisConfig,
      yAxis,
      series,
      tooltip: {},
      ...(isJourneyTouchpoints && {
        plotOptions: {
          timeline: {
            ignoreHiddenPoint: false,
            dataLabels: { allowOverlap: true },
          },
        },
      }),
    };

    const zoomOptions = ChartDataService.getZoomButtonOption(widget, result);
    if (zoomOptions) {
      Object.keys(zoomOptions).forEach((k) => (result[k] = zoomOptions[k]));
    }

    return result;
  }

  private static getZoomButtonOption(widget: Widget<WidgetChartOptions>, _result) {
    if (
      widget.payload.zoomDateFilter &&
      widget.payload.filters.dates.and.findIndex(
        (filter) => filter.value === widget.payload.zoomDateFilter
      ) !== -1
    ) {
      return {
        exporting: {
          buttons: {
            contextButton: {
              enabled: false,
            },
          },
        },
        navigation: {
          buttonOptions: {
            theme: {
              'stroke-width': 2,
              stroke: '#1274d1',
            },
          },
        },
      };
    }
  }

  public static forScatterPlot(
    _dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    _options: any
  ): HighchartsOptions {
    if (!response.items) {
      return null;
    }

    const series: {
      pointId: string;
      name: string;
      data: { x: number; y: number; change?: { x: number; y: number } }[];
      path: any;
    }[] = [];

    const currentValueExtractorFn = (isAxisImpact) => {
      if (isAxisImpact) {
        return (item) =>
          Utils.round(response.impactData.data.find((ipd) => ipd.label === item).score, 2);
      }
      return (item, axis: 'x' | 'y') => {
        const currentItems = response.currentPeriodData?.items ?? response.items;

        return Utils.round(currentItems[item]?.[axis] ?? 0, 2);
      };
    };

    const previousValueExtractorFn = (isAxisImpact) => {
      if (isAxisImpact) {
        return (item) =>
          Utils.round(
            response.impactData.data.find((ipd) => ipd.label === item)?.previousScore ?? 0,
            2
          );
      }
      return (item, axis: 'x' | 'y') => Utils.round(response.items[item]?.[axis] ?? 0, 2);
    };

    const isXAxisImpact = widget.payload.xAxis.includes('impact');
    const isYAxisImpact = widget.payload.yAxis.includes('impact');

    const currentXValueExtractor = currentValueExtractorFn(isXAxisImpact);
    const currentYValueExtractor = currentValueExtractorFn(isYAxisImpact);

    for (const key of Object.getOwnPropertyNames(response.items)) {
      let displayName = key;
      let path;
      if (response.extra?.taxonomyNodes?.[key]) {
        displayName = response.extra.taxonomyNodes[key].name;
        path = response.extra.taxonomyNodes[key]?.path;
      }

      const result = {
        pointId: key,
        name: displayName,
        data: [
          {
            id: key,
            x: currentXValueExtractor(key, 'x'),
            y: currentYValueExtractor(key, 'y'),
            documentCount: (response.currentPeriodData?.items ?? response.items)?.[key]?.[
              'docCount'
            ],
            change: undefined,
          },
        ],
        path,
      };

      if (!Number.isFinite(result.data[0].x) || !Number.isFinite(result.data[0].y)) {
        continue;
      }

      if (response.currentPeriodData) {
        const previousXValueExtractor = previousValueExtractorFn(isXAxisImpact);
        const previousYValueExtractor = previousValueExtractorFn(isYAxisImpact);

        result.data[0].change = {
          x: Utils.round(result.data[0].x - previousXValueExtractor(key, 'x'), 2),
          y: Utils.round(result.data[0].y - previousYValueExtractor(key, 'y'), 2),
          documentCount: result.data[0].documentCount - response.items?.[key]?.['docCount'],
        };
      }

      if (response.comparisonBuckets?.[1]?.items[key]) {
        result.data[0].change = {
          x: Utils.round(result.data[0].x - response.comparisonBuckets[1].items[key].x, 2),
          y: Utils.round(result.data[0].y - response.comparisonBuckets[1].items[key].y, 2),
          documentCount:
            result.data[0].documentCount - response.comparisonBuckets[1].items[key].docCount,
        };
      }

      series.push(result);
    }

    const xAxisName = ScatterPlotUtils.getAxisName(widget.payload.xAxis);
    const yAxisName = ScatterPlotUtils.getAxisName(widget.payload.yAxis);

    const isXAxisFrequency = widget.payload.xAxis === 'frequency';
    const isYAxisFrequency = widget.payload.yAxis === 'frequency';

    const result = {
      chart: {
        type: 'scatter',
        zoomType: 'xy',
      },
      xAxis: {
        title: {
          text: xAxisName,
        },
        labels: {
          format: undefined,
        },
      },
      yAxis: {
        title: {
          text: yAxisName,
        },
        labels: {
          format: undefined,
        },
      },
      series,
      plotOptions: {
        scatter: {
          marker: {
            radius: 6,
          },
        },
      },
      tooltip: {
        formatter() {
          const path = this.series.userOptions.path
            ? `${translate('widgets.charting.tooltip.path-part', {
                path: this.series.userOptions.path,
              })}<br/>`
            : '';
          const xSuffix = isXAxisFrequency ? '%' : '';
          const ySuffix = isYAxisFrequency ? '%' : '';
          const xChange = this.point.change
            ? `(${this.point.change.x > 0 ? '+' : ''}${this.point.change.x})`
            : '';
          const yChange = this.point.change
            ? `(${this.point.change.y > 0 ? '+' : ''}${this.point.change.y})`
            : '';

          return (
            `<span style="color: ${this.series.color};">${AbstractChartReport.getSeriesSymbolChar(
              this.series.symbol
            )}</span> ${this.series.name}<br/>` +
            `${path}` +
            `<b>${xAxisName}: </b>${Utils.addThousandSeparator(
              Utils.round(this.point.x, 2)
            )}${xSuffix} ${xChange}<br/>` +
            `<b>${yAxisName}: </b>${Utils.addThousandSeparator(
              Utils.round(this.y, 2)
            )}${ySuffix} ${yChange}<br/>`
          );
        },
      },
    };

    if (isXAxisFrequency) {
      result.xAxis.labels.format = '{value}%';
    }

    if (isYAxisFrequency) {
      result.yAxis.labels.format = '{value}%';
    }

    return result;
  }

  public static forBreakdown(
    _dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    _options: any
  ): HighchartsOptions {
    if (!response.items) {
      return null;
    }

    const showAsPercentage = widget.payload.display?.showAsPercentage === true;

    const getSeries = (name: string, rgba?: RGBAColorInput, fieldName?: string): any => {
      let seriesName = name;
      if (fieldName) {
        seriesName = `${FieldService.getFieldLongName(fieldName)} (${name})`;
      }
      const series = { name: seriesName, color: '', data: [] };

      if (rgba) {
        const color = `rgba(${rgba.red}, ${rgba.green}, ${rgba.blue}, ${rgba.alpha})`;
        series.color = color;
      }

      return series;
    };

    const getData = (pointId: string, y: number) => ({
      // Fix: The values must be escaped in order to be displayed correctly (e.g. <blank>).
      pointId: Utils.escapeHtml(pointId),
      y,
    });

    const categories = Object.getOwnPropertyNames(response.items);

    const getReadableScoringName = (breakdownOpts: Partial<BreakdownOptions>) => {
      return breakdownOpts.numericMetadataFieldId === 'docs'
        ? 'Count'
        : (() => {
            switch (breakdownOpts.scoring) {
              case 'avg':
                return 'Average';
              case 'sum':
                return 'Sum';
              case 'nps':
                return 'NPS';
              case 'box':
                return 'Box';
              case 'bucket':
                return 'Buckets';
            }
          })();
    };

    const isBoxScoring = (breakdownOpts: Partial<BreakdownOptions>) =>
      breakdownOpts.scoring === 'box';

    const isBucketScoring = (breakdownOpts: Partial<BreakdownOptions>) =>
      breakdownOpts.scoring === 'bucket';

    const series = [];
    const bucketSeriesKeyToIndex = new Map<string, number>();
    const getBucketSeriesKey = (payloadProp: string, bucketIndex: number): string =>
      `${payloadProp}:${bucketIndex}`;

    for (const key of ['breakdownOptions', 'additionalBreakdownOptions']) {
      const breakdownOptions = widget.payload[key];
      if (!breakdownOptions) {
        continue;
      }
      const scoring = breakdownOptions.scoring;
      const fieldName =
        (widget.payload.additionalBreakdownOptions && breakdownOptions.numericMetadataFieldId) ??
        '';
      if (isBucketScoring(breakdownOptions)) {
        const buckets = Array.isArray(breakdownOptions.fieldBuckets)
          ? breakdownOptions.fieldBuckets
          : [];
        buckets.forEach((bucket, bucketIndex) => {
          const bucketSeries = getSeries(bucket.bucketName, bucket.color, fieldName);
          bucketSeries.custom = {
            scoring,
            bucketIndex,
            payloadProp: key,
          };
          if (showAsPercentage) {
            bucketSeries.tooltip = {
              ...(bucketSeries.tooltip ?? {}),
              valueSuffix: '%',
            };
          }
          series.push(bucketSeries);
          bucketSeriesKeyToIndex.set(getBucketSeriesKey(key, bucketIndex), series.length - 1);
        });
      } else if (isBoxScoring(breakdownOptions)) {
        if (breakdownOptions.isTopShown) {
          const topSeries = getSeries('Top', breakdownOptions.topColor, fieldName);
          topSeries.custom = {
            scoring,
            box: 'top',
            payloadProp: key,
          };
          series.push(topSeries);
        }

        if (breakdownOptions.isNeutralShown) {
          const neutralSeries = getSeries('Neutral', breakdownOptions.neutralColor, fieldName);
          neutralSeries.custom = {
            scoring,
            box: 'neutral',
            payloadProp: key,
          };
          series.push(neutralSeries);
        }

        if (breakdownOptions.isBottomShown) {
          const bottomSeries = getSeries('Bottom', breakdownOptions.bottomColor, fieldName);
          bottomSeries.custom = {
            scoring,
            box: 'bottom',
            payloadProp: key,
          };
          series.push(bottomSeries);
        }
      } else {
        const seriesName = getReadableScoringName(breakdownOptions);
        const serie = {
          ...getSeries(seriesName, null, fieldName),
          yAxis: 0,
          custom: {
            scoring,
            payloadProp: key,
          },
          ...(widget.payload.hasSecondaryAxis &&
            key === 'additionalBreakdownOptions' && {
              type: 'line',
              marker: {
                lineWidth: 1,
              },
              yAxis: 1,
            }),
        };
        series.push(serie);
      }
    }

    const boxScoringHandler = (
      opts: Partial<BreakdownOptions>,
      seriesIndex: number,
      category: string,
      value: any,
      idx: number,
      stackName?: string
    ) => {
      const commonFields = {
        yAxis: idx,
        stacking: 'normal',
        groupPadding: 0,
        documentCount: value.docs,
        ...(stackName && { stack: stackName }),
      };
      if (opts.isTopShown) {
        const topSerieIdx = seriesIndex++;
        series[topSerieIdx] = {
          ...series[topSerieIdx],
          ...commonFields,
        };
        series[topSerieIdx].data.push({
          ...getData(category, value.topScore),
        });
      }

      if (opts.isNeutralShown) {
        const neutralSerieIdx = seriesIndex++;
        series[neutralSerieIdx] = {
          ...series[neutralSerieIdx],
          ...commonFields,
        };
        series[neutralSerieIdx].data.push({
          ...getData(category, value.neutralScore),
        });
      }

      if (opts.isBottomShown) {
        const bottomSerieIdx = seriesIndex++;
        series[bottomSerieIdx] = {
          ...series[bottomSerieIdx],
          ...commonFields,
        };
        series[bottomSerieIdx].data.push({
          ...getData(category, value.bottomScore),
        });
      }
    };

    const bucketScoringHandler = (
      payloadProp: string,
      opts: Partial<BreakdownOptions>,
      category: string,
      value: any,
      idx: number,
      stackName?: string
    ): void => {
      const bucketValues: unknown = value?.bucketValues;
      const bucketScores: unknown = value?.bucketScores;

      const getBucketMetricValue = (
        container: unknown,
        bucketIndex: number,
        bucketName: string
      ): number => {
        if (Array.isArray(container)) {
          const v = container[bucketIndex];
          return typeof v === 'number' ? v : 0;
        }
        if (typeof container === 'object' && container !== null) {
          const v = (container as Record<string, unknown>)[bucketName];
          return typeof v === 'number' ? v : 0;
        }
        return 0;
      };

      const buckets = Array.isArray(opts.fieldBuckets) ? opts.fieldBuckets : [];
      buckets.forEach((bucket, bucketIndex) => {
        const serieIdx = bucketSeriesKeyToIndex.get(getBucketSeriesKey(payloadProp, bucketIndex));
        if (serieIdx === undefined) {
          return;
        }
        series[serieIdx] = {
          ...series[serieIdx],
          yAxis: idx,
          stacking: 'normal',
          groupPadding: 0,
          ...(stackName && { stack: stackName }),
        };
        const bucketDocumentCount = getBucketMetricValue(
          bucketValues,
          bucketIndex,
          bucket.bucketName
        );
        const bucketValue = showAsPercentage
          ? getBucketMetricValue(bucketScores, bucketIndex, bucket.bucketName)
          : bucketDocumentCount;
        series[serieIdx].data.push({
          ...getData(category, bucketValue),
          documentCount: bucketDocumentCount,
        });
      });
    };

    const { breakdownOptions, additionalBreakdownOptions } = widget.payload;
    for (const category of categories) {
      const values = response.items[category].metadata;
      for (const [index, value] of values.entries()) {
        const isBothMetricsBoxScoring =
          breakdownOptions.scoring === 'box' && additionalBreakdownOptions?.scoring === 'box';
        const isBothMetricsBucketScoring =
          breakdownOptions.scoring === 'bucket' && additionalBreakdownOptions?.scoring === 'bucket';

        // We select the correct options and axis values
        const currentOptions = index === 0 ? breakdownOptions : additionalBreakdownOptions;
        const currentYAxis =
          !isBothMetricsBoxScoring && !isBothMetricsBucketScoring && index !== 0 ? index : 0;

        let seriesIndex = 0; // Default value, it can change when scoring is box

        if (!isBoxScoring(currentOptions) && !isBucketScoring(currentOptions)) {
          const nonBoxSeriesIndex = series.length > 2 ? series.findIndex((s) => !s.color) : index;
          if (value.score !== 'NaN') {
            series[nonBoxSeriesIndex].data.push({
              ...getData(category, value.score),
              yAxis: currentYAxis,
              documentCount: value.docs,
            });
          }
        } else if (isBoxScoring(currentOptions)) {
          // Handling box scoring
          if (index === 0) {
            seriesIndex = 0; // Primary metric box scoring
          } else {
            // Secondary metric box scoring
            seriesIndex = isBothMetricsBoxScoring ? 3 : 1;
          }

          boxScoringHandler(
            currentOptions,
            seriesIndex,
            category,
            value,
            currentYAxis,
            isBothMetricsBoxScoring ? `group${index}` : ''
          );
        } else {
          const payloadProp = index === 0 ? 'breakdownOptions' : 'additionalBreakdownOptions';
          bucketScoringHandler(
            payloadProp,
            currentOptions,
            category,
            value,
            currentYAxis,
            isBothMetricsBucketScoring ? `group${index}` : ''
          );
        }
      }
    }

    series.forEach((s) =>
      s.data.forEach((d) => {
        d.id = `${d.pointId}-${s.name}`;
      })
    );

    const hasValidData = series.some((s) =>
      s.data.some((d) => typeof d.y === 'number' && !isNaN(d.y))
    );

    // If no valid data, clear the series data to prevent infinite spinner
    if (!hasValidData) {
      series.forEach((s) => (s.data = []));
    }

    let yAxisTitle = 'Values';
    if (breakdownOptions.scoring === 'bucket') {
      yAxisTitle = showAsPercentage
        ? translate('widgets.charting.documents-percentage')
        : translate('widgets.charting.document-count');
    }

    const result: any = {
      chart: {
        type: widget.payload.graphType,
      },
      xAxis: {
        categories,
      },
      yAxis: {
        title: {
          text: yAxisTitle,
        },
      },
      series,
      tooltip: {},
    };

    if (breakdownOptions.scoring === 'box') {
      result.yAxis.max = 100;
      result.plotOptions = {
        series: {
          stacking: 'normal',
        },
      };
    }

    if (breakdownOptions.scoring === 'bucket') {
      result.plotOptions = {
        ...(result.plotOptions ?? {}),
        series: {
          ...(result.plotOptions?.series ?? {}),
          stacking: 'normal',
        },
      };

      if (showAsPercentage) {
        result.yAxis.max = 100;
        result.yAxis.labels = {
          ...(result.yAxis.labels ?? {}),
          format: '{value}%',
        };
      }
    }

    if (additionalBreakdownOptions && widget.payload.hasSecondaryAxis) {
      const getTitleForAxis = (breakdownOpts: Partial<BreakdownOptions>) => {
        if (breakdownOpts.scoring === 'bucket') {
          return showAsPercentage
            ? translate('widgets.charting.documents-percentage')
            : translate('widgets.charting.document-count');
        }
        return `${FieldService.getFieldLongName(
          breakdownOpts.numericMetadataFieldId
        )} (${getReadableScoringName(breakdownOpts)})`;
      };
      result.yAxis = [
        { ...result.yAxis },
        {
          opposite: true,
          ...(additionalBreakdownOptions.scoring === 'box' && { max: 100 }),
          ...(showAsPercentage && additionalBreakdownOptions.scoring === 'bucket' && { max: 100 }),
        },
      ].map((axis, idx) => ({
        ...axis,
        title: {
          text: getTitleForAxis(idx === 0 ? breakdownOptions : additionalBreakdownOptions),
        },
      }));

      if (showAsPercentage && additionalBreakdownOptions.scoring === 'bucket') {
        (result.yAxis[1] as any).labels = {
          ...((result.yAxis[1] as any).labels ?? {}),
          format: '{value}%',
        };
      }
    }
    if (
      additionalBreakdownOptions?.scoring === 'box' ||
      additionalBreakdownOptions?.scoring === 'bucket'
    ) {
      result.plotOptions = {
        ...(result.plotOptions ?? {}),
        column: {
          ...(result.plotOptions?.column ?? {}),
          grouping: true, // Ensure columns are grouped
        },
        series: {
          ...(result.plotOptions?.series ?? {}),
          pointPadding: 0.1,
          groupPadding: 0.1, // Adjusts the space between the groups
        },
      };
    }
    return result;
  }
  public static forGauge(
    dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    comparisonService: ComparisonHandlerService,
    reportOptions?: WidgetChartOptions
  ): HighchartsOptions {
    if (!response.items) {
      return null;
    }

    const gaugeOptions = widget.payload.gaugeOptions;
    const getScore = (metricData): number => {
      if (gaugeOptions.metric === 'box') {
        if (gaugeOptions.metricBoxType === 'top') {
          return metricData.topScore;
        } else {
          return metricData.bottomScore;
        }
      } else {
        return metricData.score;
      }
    };

    const score = getScore(response.items.metric);
    const decimalPrecision =
      gaugeOptions.decimalPrecision ?? ChartOptionConstants.DEFAULT_DECIMAL_PRECISION;

    const hasComparison2Filter = response.comparisonBuckets?.length === 2;
    let comparisonData: GaugeComparisonData;
    if (hasComparison2Filter) {
      const metricValue = getScore(response.comparisonBuckets[1].items.metric);
      const comparisonDiff = score - metricValue;
      const isIncrease = comparisonDiff > 0;

      let metricText: string;
      if (gaugeOptions.showAs === 'absolute') {
        metricText = Utils.round(comparisonDiff, decimalPrecision).toString();
      } else {
        // excluding NaN situations when both numerator and denominator are zero
        const pctValue = (score === 0 ? comparisonDiff : comparisonDiff / score) * 100;
        metricText = `${Utils.round(pctValue, decimalPrecision)}%`;
      }

      const colorPref = isIncrease ? gaugeOptions.increaseColor : gaugeOptions.decreaseColor;
      let iconColor: string;
      switch (colorPref) {
        case 'gray':
          iconColor = '#444444';
          break;
        case 'green':
          iconColor = '#147255';
          break;
        case 'red':
          iconColor = '#AE2E29';
          break;
      }

      comparisonData = { metricValue, metricText, iconColor, isIncrease };
    }

    return ChartDataService.buildGaugeResult({
      dashboard,
      widget,
      response,
      comparisonService,
      reportOptions,
      score,
      comparisonData,
      isDualGauge: hasComparison2Filter,
    });
  }

  private static buildGaugeResult(params: {
    dashboard: Dashboard;
    widget: Widget<WidgetChartOptions>;
    response: any;
    comparisonService: ComparisonHandlerService;
    reportOptions: WidgetChartOptions;
    score: number;
    comparisonData: GaugeComparisonData;
    isDualGauge: boolean;
  }): HighchartsOptions {
    const gaugeOptions = params.widget.payload.gaugeOptions;
    const decimalPrecision =
      gaugeOptions.decimalPrecision ?? ChartOptionConstants.DEFAULT_DECIMAL_PRECISION;
    const { min, max } = ChartDataService.getGaugeMinMax(gaugeOptions);
    const metricText = Utils.round(params.score, decimalPrecision).toString();
    const fieldName = FieldService.getFieldLongName(params.response.items.metric.field);
    const fieldId = params.response.items.metric.field;
    const boxSuffix = gaugeOptions.metric === 'box' ? '%' : '';
    const docCount =
      params.reportOptions?.currentPeriodDocumentCount ??
      params.reportOptions?.comparisonBucketsCounts?.[0] ??
      params.reportOptions?.filteredDocumentCount;
    const comparison = params.isDualGauge
      ? params.dashboard.comparisons[params.widget.payload.comparison]
      : undefined;

    const clamp = (val: number): number => Math.max(min, Math.min(max, val));

    const gaugeEntries = ChartDataService.buildGaugeEntries(
      {
        score: params.score,
        metricText,
        docCount,
        comparisonMetricValue: params.comparisonData?.metricValue,
        comparisonBucketsCounts: params.reportOptions?.comparisonBucketsCounts,
        isDualGauge: params.isDualGauge,
        comparison,
        decimalPrecision,
        min,
        max,
      },
      { fieldName, fieldId, clamp }
    );

    const withinLimits = gaugeEntries.every((e) => e.withinLimit);
    const metadataSelectionErrorMsg = !withinLimits
      ? translate('widgets.numericMetaDataScoreAboveScope')
      : '';

    const series = [];
    const plotBandsPerAxis: any[][] = gaugeEntries.map(() => []);

    gaugeEntries.forEach((entry, idx) => {
      if (entry.metricText === 'NaN' || entry.metricText === 'Infinity') {
        return;
      }
      const seriesEntry: any = {
        dial: {
          radius: '105%',
          backgroundColor: entry.withinLimit ? 'black' : 'red',
          baseWidth: 10,
          baseLength: '0%',
          rearLength: '0%',
        },
        dataLabels: {
          allowOverlap: true,
          format:
            `${entry.metricText} ${boxSuffix}` +
            '<br/><span style="color:gray;fontSize:12px;' +
            'fontWeight:200;"> n = ' +
            Utils.addThousandSeparator(entry.docCount) +
            '</span>',
          borderWidth: 0,
          y: 3,
          style: { fontSize: '25px' },
        },
        data: entry.data,
      };
      if (params.isDualGauge) {
        seriesEntry.yAxis = idx;
      }
      series.push(seriesEntry);
      plotBandsPerAxis[idx].push(
        {
          from: min,
          to: entry.gaugeRating,
          color: entry.color,
          thickness: '100%',
        },
        {
          from: entry.gaugeRating,
          to: max,
          color: '#dddddd',
          thickness: '100%',
        }
      );
    });

    const yAxisBase = {
      min,
      max,
      tickPixelInterval: 0,
      tickWidth: 0,
      minorTickInterval: null,
      labels: {
        distance: 20,
        style: { fontSize: '14px' },
      },
      lineWidth: 0,
    };

    const buildGaugePane = (center: string[], size: string) => ({
      startAngle: -90,
      endAngle: 89.9,
      background: null,
      center,
      size,
      innerSize: 0,
    });

    const result: HighchartsOptions = {
      chart: {
        type: 'gauge',
        plotShadow: false,
        events: {},
      },
      subtitle: {
        text: metadataSelectionErrorMsg,
        align: 'center',
        verticalAlign: 'bottom',
        style: { fontSize: '12px' },
        y: -10,
      },
      pane: buildGaugePane(['50%', '75%'], '100%'),
      xAxis: {},
      yAxis: { ...yAxisBase, plotBands: plotBandsPerAxis[0] },
      series,
      tooltip: {},
    };
    if (params.isDualGauge) {
      result.chart.spacing = [10, 10, 40, 10];
      const extractor = params.comparisonService.filterDisplayValueExtractorFn(comparison?.type);
      result.chart.events = ChartDataService.buildDualGaugeRenderEvent({
        comparisonData: params.comparisonData,
        filterName1: extractor?.(comparison?.filters[0]) ?? '',
        filterName2: extractor?.(comparison?.filters[1]) ?? '',
      });
      result.pane = [buildGaugePane(['25%', '70%'], '60%'), buildGaugePane(['75%', '70%'], '60%')];
      result.yAxis = gaugeEntries.map((_, idx) => ({
        ...yAxisBase,
        pane: idx,
        plotBands: plotBandsPerAxis[idx],
      }));
    }
    return result;
  }

  private static getGaugeMinMax(gaugeOptions: any): { min: number; max: number } {
    switch (gaugeOptions.metric) {
      case MetricEnum.BOX:
        return { min: gaugeOptions.min ?? 0, max: gaugeOptions.max ?? 100 };
      case MetricEnum.AVG:
        return { min: gaugeOptions.min ?? 0, max: gaugeOptions.max ?? 10 };
      case MetricEnum.NPS:
        return { min: gaugeOptions.min ?? -100, max: gaugeOptions.max ?? 100 };
    }
  }

  private static buildGaugeEntries(
    params: {
      score: number;
      metricText: string;
      docCount: number;
      comparisonMetricValue: number;
      comparisonBucketsCounts: number[];
      isDualGauge: boolean;
      comparison: any;
      decimalPrecision: number;
      min: number;
      max: number;
    },
    helpers: {
      fieldName: string;
      fieldId: string;
      clamp: (val: number) => number;
    }
  ): {
    gaugeRating: number;
    withinLimit: boolean;
    metricText: string;
    docCount: number;
    color: string;
    data: any[];
  }[] {
    const primaryEntry = {
      gaugeRating: helpers.clamp(params.score),
      withinLimit: params.score <= params.max && params.score >= params.min,
      metricText: params.metricText,
      docCount: params.docCount,
      color: '#3471f5',
      data: [
        {
          y: helpers.clamp(params.score),
          name: helpers.fieldName,
          id: helpers.fieldId,
          value: params.metricText,
          documentCount: params.docCount,
        },
      ],
    };

    if (!params.isDualGauge) {
      return [primaryEntry];
    }

    const filterColor1 = params.comparison?.filters[0]?.color ?? DEFAULT_COMPARISON_COLOR_STRING;
    const filterColor2 = params.comparison?.filters[1]?.color ?? DEFAULT_COMPARISON_COLOR_STRING;
    primaryEntry.color = filterColor1;

    const compScore = params.comparisonMetricValue;
    const compMetricText = Utils.round(compScore, params.decimalPrecision).toString();
    const compDocCount = params.comparisonBucketsCounts?.[1];

    const secondaryEntry = {
      gaugeRating: helpers.clamp(compScore),
      withinLimit: compScore <= params.max && compScore >= params.min,
      metricText: compMetricText,
      docCount: compDocCount,
      color: filterColor2,
      data: [
        {
          y: helpers.clamp(compScore),
          name: helpers.fieldName,
          id: helpers.fieldId + '_comparison',
          value: compMetricText,
          documentCount: compDocCount,
        },
      ],
    };

    return [primaryEntry, secondaryEntry];
  }

  private static buildDualGaugeRenderEvent(params: {
    comparisonData: GaugeComparisonData;
    filterName1: string;
    filterName2: string;
  }): { render(): void } {
    const { comparisonData, filterName1, filterName2 } = params;

    const renderLabel = (renderer: any, text: string, x: number, y: number): any =>
      renderer
        .text(text, x, y, true)
        .css({ color: '#000', fontSize: '16px', fontWeight: 'bold' })
        .attr({ align: 'center' })
        .add();

    const buildTrianglePath = (
      cx: number,
      baseY: number,
      size: number,
      pointsUp: boolean
    ): (string | number)[] => {
      const hw = size * 0.7;
      return pointsUp
        ? ['M', cx, baseY - size, 'L', cx - hw, baseY, 'L', cx + hw, baseY, 'Z']
        : ['M', cx - hw, baseY - size, 'L', cx + hw, baseY - size, 'L', cx, baseY, 'Z'];
    };

    return {
      render() {
        const chart = this;
        chart.comparisonLabel1?.destroy();
        chart.comparisonLabel2?.destroy();
        chart.comparisonMetric?.destroy();
        chart.comparisonIcon?.destroy();

        const { plotWidth, plotLeft, plotTop, plotHeight } = chart;
        const labelY = plotTop + 20;

        chart.comparisonLabel1 = renderLabel(
          chart.renderer,
          filterName1,
          plotLeft + plotWidth * 0.25,
          labelY
        );
        chart.comparisonLabel2 = renderLabel(
          chart.renderer,
          filterName2,
          plotLeft + plotWidth * 0.75,
          labelY
        );

        const deltaY = plotTop + plotHeight + 5;
        const iconSize = 14;
        const iconCenterX = plotLeft + plotWidth / 2 - 30;

        chart.comparisonIcon = chart.renderer
          .path(buildTrianglePath(iconCenterX, deltaY, iconSize, comparisonData.isIncrease))
          .attr({ fill: comparisonData.iconColor })
          .add()
          .toFront();

        chart.comparisonMetric = chart.renderer
          .text(comparisonData.metricText, iconCenterX + iconSize + 8, deltaY, true)
          .css({ color: '#000', fontSize: '20px' })
          .attr({ align: 'left' })
          .add();
      },
    };
  }

  public static forDonut(
    dashboard: Dashboard,
    widget: Widget<WidgetChartOptions>,
    response: any,
    _options: any,
    comparisonService: ComparisonHandlerService,
    reportOptions?: WidgetChartOptions
  ): HighchartsOptions {
    const isMetricScoreInvalid = (metricData): boolean => {
      return (
        metricData?.score === 'NaN' ||
        metricData?.score === 'Infinity' ||
        metricData?.score === null
      );
    };

    if (!response.items) {
      return null;
    }

    const donutOptions = widget.payload.donutOptions;

    const getScore = (metricData): number => {
      if (donutOptions.metric === 'box') {
        if (donutOptions.metricBoxType === 'top') {
          return metricData.topScore;
        } else {
          return metricData.bottomScore;
        }
      } else {
        return metricData.score;
      }
    };

    const decimalPrecision =
      donutOptions.decimalPrecision ?? ChartOptionConstants.DEFAULT_DECIMAL_PRECISION;

    const metric = getScore(response.items.metric);
    let metricText = Utils.round(metric, decimalPrecision).toString();

    const hasComparison2Filter = response.comparisonBuckets?.length === 2;
    let comparisonMetricText: string;
    let comparisonIconColor: string;
    let isComparisonIncrease: boolean;
    if (hasComparison2Filter) {
      const comparisonMetric = getScore(response.comparisonBuckets[1].items.metric);
      const comparisonDiff = metric - comparisonMetric;

      if (donutOptions.showAs === 'absolute') {
        comparisonMetricText = Utils.round(comparisonDiff, decimalPrecision).toString();
      } else {
        comparisonMetricText = `${Utils.round((comparisonDiff / metric) * 100, decimalPrecision)}%`;
      }

      isComparisonIncrease = comparisonDiff > 0;
      const colorPref = isComparisonIncrease
        ? donutOptions.increaseColor
        : donutOptions.decreaseColor;
      switch (colorPref) {
        case 'gray':
          comparisonIconColor = '#444444';
          break;
        case 'green':
          comparisonIconColor = '#147255';
          break;
        case 'red':
          comparisonIconColor = '#AE2E29';
          break;
      }
    }

    const hasComparison = !!widget.payload.comparison;
    let comparisonBucketNames: string;
    if (hasComparison) {
      const comparison = dashboard.comparisons[widget.payload.comparison];
      const sb = new StringBuilder();
      const extractor = comparisonService.filterDisplayValueExtractorFn(comparison?.type);

      comparison?.filters.forEach((fs) => {
        if (sb.length) {
          sb.append(' VS: ');
        }
        sb.append(extractor(fs));
      });

      comparisonBucketNames = sb.toString();
    }

    const docCount =
      reportOptions?.currentPeriodDocumentCount ??
      reportOptions?.comparisonBucketsCounts?.[0] ??
      reportOptions?.filteredDocumentCount;

    const series = [];
    const data = [];
    let categories = [];

    if (donutOptions.scoring === 'box') {
      const addData = (name: string, rgba: RGBAColorInput) => {
        const donutBox = response.items.donutBox;
        const point: any = {
          name: Utils.titleCase(name),
          pointId: name,
          id: name,
          y: donutBox[`${name}Score`],
          documentCount: donutBox[`${name}Value`],
          color: `rgba(${rgba.red}, ${rgba.green}, ${rgba.blue}, ${rgba.alpha})`,
        };

        if (hasComparison2Filter) {
          const comparisonDonutBox = response.comparisonBuckets[1].items.donutBox;
          point.custom = {
            comparisonDocumentCount: comparisonDonutBox[`${name}Value`],
            comparisonY: comparisonDonutBox[`${name}Score`],
          };
        }

        data.push(point);

        categories.push(name);
      };

      if (donutOptions.isTopShown) {
        addData('top', donutOptions.topColor);
      }

      if (donutOptions.isNeutralShown) {
        addData('neutral', donutOptions.neutralColor);
      }

      if (donutOptions.isBottomShown) {
        addData('bottom', donutOptions.bottomColor);
      }
    } else {
      const donutRating = response.items.donutRating;

      Object.getOwnPropertyNames(donutRating).forEach((key) => {
        const point: any = {
          name: key,
          y: donutRating[key],
          pointId: key,
          id: key,
          documentCount: donutRating[key],
        };

        if (hasComparison2Filter) {
          point.custom = {
            comparisonDocumentCount: response.comparisonBuckets[1].items.donutRating[key],
            comparisonY: response.comparisonBuckets[1].items.donutRating[key],
          };
        }

        data.push(point);
      });

      data.sort((a, b) => b.y - a.y);
      categories = data.map((d) => d.name);
    }

    if (isMetricScoreInvalid(response.items.metric)) {
      metricText = translate('widgets.charting.no-value');
    }

    if (metricText !== 'NaN' && metricText !== 'Infinity') {
      const seriesData: any = {
        innerSize: '75%',
        data,
        custom: {
          documentCount: docCount,
        },
      };

      if (hasComparison2Filter) {
        seriesData.custom.comparisonDocumentCount = reportOptions.comparisonBucketsCounts[1];
      }

      series.push(seriesData);
    }

    const result = {
      chart: {
        type: 'pie',
        events: {
          render() {
            const chart = this;
            const x = chart.plotLeft + chart.series[0].center[0];
            const y = chart.plotTop + chart.series[0].center[1];

            chart.metric?.destroy();
            chart.comparisonMetric?.destroy();
            chart.comparisonIcon?.destroy();
            chart.docCount?.destroy();

            // Derive sizes from donut radius so labels scale with chart size
            const donutLabels = ChartDataService.DONUT_LABELS;
            const donutSeries: any = chart.series[0];
            const getDonutRadius = (ser: any): number => {
              const radiusFromShapeArgs = ser.points?.[0]?.shapeArgs?.r;
              if (typeof radiusFromShapeArgs === 'number') {
                return radiusFromShapeArgs;
              }
              const diameter =
                Array.isArray(ser.center) && typeof ser.center[2] === 'number'
                  ? ser.center[2]
                  : undefined;
              return typeof diameter === 'number' ? diameter / 2 : donutLabels.DEFAULT_RADIUS;
            };
            const derivedRadius = getDonutRadius(donutSeries);
            const metricFont = Math.round(
              Math.min(
                donutLabels.METRIC_FONT_MAX,
                Math.max(donutLabels.METRIC_FONT_MIN, derivedRadius * donutLabels.METRIC_FONT_RATIO)
              )
            );
            const docFont = Math.round(
              Math.min(
                donutLabels.docCountFontMax,
                Math.max(donutLabels.docCountFontMin, metricFont * donutLabels.docCountFontRatio)
              )
            );
            const docYOffset = Math.round(metricFont * donutLabels.docCountYOffsetRatio);
            const compYOffset = Math.round(metricFont * donutLabels.comparisonMetricYOffsetRatio);
            const gap = Math.round(
              Math.max(
                donutLabels.GAP_MIN,
                Math.min(donutLabels.GAP_MAX, metricFont * donutLabels.GAP_RATIO)
              )
            );

            chart.metric = chart.renderer
              .text(metricText, x, y, true)
              .css({
                color: 'black',
                fontSize: `${metricFont}px`,
                fontWeight: 'bold',
              })
              .add();

            chart.metric.attr({
              x: x - chart.metric.getBBox().width / 2,
            });

            if (!isMetricScoreInvalid(response.items.metric)) {
              chart.docCount = chart.renderer
                .text(`n = ${Utils.addThousandSeparator(docCount)}`, x, y - docYOffset, true)
                .css({
                  color: 'black',
                  fontSize: `${docFont}px`,
                })
                .add();

              chart.docCount.attr({
                x: x - chart.docCount.getBBox().width / 2,
              });
            }

            if (hasComparison2Filter) {
              let mx: number;
              let my: number;
              let l1x: number;
              let l1y: number;
              let l2x: number;
              let l2y: number;

              chart.comparisonMetric = chart.renderer
                .text(comparisonMetricText, x + gap, y + compYOffset, true)
                .css({
                  color: 'black',
                  fontSize: `${Math.max(
                    donutLabels.comparisonMetricFontMin,
                    Math.round(metricFont * donutLabels.comparisonMetricFontRatio)
                  )}px`,
                })
                .add();

              const metricBox = chart.comparisonMetric.htmlGetBBox() as {
                x: number;
                y: number;
                width: number;
                height: number;
              };

              if (isComparisonIncrease) {
                mx = metricBox.x - gap * 2 - metricBox.height / 2;
                my = metricBox.y;
                l1x = metricBox.x - gap * 2 - metricBox.height;
                l1y = metricBox.y + metricBox.height;
                l2x = metricBox.x - gap * 2;
                l2y = metricBox.y + metricBox.height;
              } else {
                mx = metricBox.x - gap * 2 - metricBox.height;
                my = metricBox.y;
                l1x = metricBox.x - gap * 2;
                l1y = metricBox.y;
                l2x = metricBox.x - gap * 2 - metricBox.height / 2;
                l2y = metricBox.y + metricBox.height;
              }

              chart.comparisonIcon = chart.renderer
                .path(['M', mx, my, 'L', l1x, l1y, 'L', l2x, l2y, 'Z'])
                .attr({
                  fill: comparisonIconColor,
                })
                .add()
                .toFront();
            }

            if (hasComparison && comparisonBucketNames) {
              chart.metric.element.addEventListener('mouseover', () => {
                chart.comparisonTooltip = chart.renderer
                  .text(
                    `<div class="donut-metric-tooltip">${comparisonBucketNames}</div>`,
                    x,
                    y + ChartDataService.DONUT_LABELS.TOOLTIP_Y_OFFSET,
                    true
                  )
                  .add();
                chart.comparisonTooltip.attr({
                  x: x - chart.comparisonTooltip.getBBox().width / 2,
                });
              });

              chart.metric.element.addEventListener('mouseout', () => {
                if (chart.comparisonTooltip) {
                  chart.comparisonTooltip.destroy();
                }
              });
            }

            if (!('centerDonutAnnotations' in chart)) {
              chart.centerDonutAnnotations = () => {
                if (
                  !chart.annotations ||
                  !chart.annotations.length ||
                  !Array.isArray(chart.series?.[0]?.center)
                ) {
                  return;
                }
                chart.annotations.forEach((ann) => {
                  try {
                    // Skip hidden or draggable (pinned card) annotations
                    if (!ann?.options?.visible || ann?.options?.draggable) {
                      return;
                    }
                    ann.labels?.forEach((label) => {
                      const pt: any = label?.points && label.points[0];
                      const sa: any = pt?.shapeArgs;
                      if (!sa) {
                        return;
                      }

                      const cx: number = chart.plotLeft + chart.series[0].center[0];
                      const cy: number = chart.plotTop + chart.series[0].center[1];
                      const outerR: number = sa.r;
                      const innerR: number = sa.innerR ?? outerR * 0.6;
                      const angle: number = (sa.start + sa.end) / 2;
                      const factor = 0.5; // center of the ring thickness
                      const radius = innerR + (outerR - innerR) * factor;
                      const px = cx + Math.cos(angle) * radius;
                      const py = cy + Math.sin(angle) * radius;

                      const g: any = (label as any)?.graphic;
                      if (g && typeof g.getBBox === 'function' && typeof g.attr === 'function') {
                        const bbox = g.getBBox();
                        const gx = px - (bbox.width || 24) / 2;
                        const gy = py - (bbox.height || 24) / 2;
                        g.attr({ translateX: gx, translateY: gy });
                        if (typeof g.toFront === 'function') {
                          g.toFront();
                        }
                      } else {
                        const div: HTMLElement =
                          ((label as any)?.graphic?.div as HTMLElement) ||
                          (ann.graphic?.div as HTMLElement);
                        if (div) {
                          const w = div.offsetWidth || 24;
                          const h = div.offsetHeight || 24;
                          div.style.position = 'absolute';
                          div.style.left = `${px - w / 2}px`;
                          div.style.top = `${py - h / 2}px`;
                        }
                      }
                    });
                  } catch (_) {
                    // Ignore to avoid breaking render loop
                  }
                });
              };
            }
            chart.centerDonutAnnotations();
            requestAnimationFrame(() => chart.centerDonutAnnotations?.());
            setTimeout(() => chart.centerDonutAnnotations?.(), 0);
            setTimeout(() => chart.centerDonutAnnotations?.(), 120);
          },
        },
      },
      xAxis: {
        categories,
      },
      series,
      tooltip: {},
    };

    return result;
  }
}
// FIXME: migrate tooltip to chart reports
